import I18n from 'I18n';
import enviro from 'enviro';
import { List, Range } from 'immutable';
import { parse } from 'hub-http/helpers/params';
import { pick } from 'underscore';
import { isTodayBetweenDays, isTodayOrPast } from './DateUtils';
import { isNewUser } from 'academy-common-lib/utils/url';
import { isHubspotAndroid, isHubSpotNativeApp } from 'academy-common-lib/utils/native/nativeAppDetection';
const DURATION_TRIM_ZERO_HOURS = true;
export const leftPadNumberWithAZero = number => {
  if (number < 10) {
    return `0${String(number)}`;
  }
  return String(number);
};
export const convertToMinutes = seconds => Number((seconds / 60).toFixed(3));
export const formatSecondsToTextDuration = (lengthInSeconds = 0) => {
  if (typeof lengthInSeconds !== 'number') {
    lengthInSeconds = 0;
  }
  const duration = I18n.moment.duration(Math.max(0, lengthInSeconds), 'seconds');
  const minutes = duration.minutes();
  const paddedSeconds = leftPadNumberWithAZero(duration.seconds());
  const days = duration.days();
  let hours = duration.hours();
  if (days > 0) {
    hours = hours + days * 24;
  }
  if (hours > 0) {
    return `${leftPadNumberWithAZero(hours)}:${leftPadNumberWithAZero(minutes)}:${paddedSeconds}`;
  }
  return `${DURATION_TRIM_ZERO_HOURS ? '' : '00:'}${leftPadNumberWithAZero(minutes)}:${paddedSeconds}`;
};
export const getHumanizedDuration = (lengthInSeconds, durationFormat = 'seconds') => {
  const duration = I18n.moment.duration(lengthInSeconds, durationFormat);
  const minutes = Math.round(duration.as('m'));
  if (minutes === 1 && lengthInSeconds >= I18n.moment.relativeTimeThreshold('s')) {
    return I18n.text('learningCenter.common.oneMinute');
  }
  const hours = Math.round(duration.as('h'));
  if (hours === 1 && minutes >= I18n.moment.relativeTimeThreshold('m')) {
    return I18n.text('learningCenter.common.oneHour');
  }
  const days = Math.round(duration.as('d'));
  if (days === 1 && hours >= I18n.moment.relativeTimeThreshold('h')) {
    return I18n.text('learningCenter.common.oneDay');
  }
  return duration.humanize();
};
export const toKeyMirror = arr => arr.reduce((memo, item) => Object.assign(memo, {
  [item]: item
}), {});
export const makeKeyed = items => Object.keys(items).reduce((memo, item) => Object.assign(memo, {
  [item]: toKeyMirror(items[item])
}), {});

// Similar to immutable interleave, except this does not stop at the end of the
// shortest list.
// See https://devdocs.io/immutable/index#list.interleave
export const interleaveAll = lists => Range(0, lists.map(list => list.size).max() || 0).flatMap(index => lists.reduce((acc, list) => list.has(index) ? acc.push(list.get(index)) : acc, List())).toList();
export const groupRecommendationIds = recommendations => List(recommendations.groupBy(recommendation => recommendation.get('recommendationStrategy')).map(group => group.map(recommendation => recommendation.get('recommendationId').toString())).valueSeq());
export const flattenRecommendations = recommendations => interleaveAll(groupRecommendationIds(recommendations));
export const safeSmoothScrollTo = (el, left, top) => {
  el.scrollTo({
    top,
    left,
    behavior: 'smooth'
  });
};
export const safeScrollTo = (el, left, top) => {
  if ('scrollBehavior' in document.documentElement.style) {
    el.scrollTo({
      top,
      left
    });
  } else {
    el.scrollTop = top;
    el.scrollLeft = left;
  }
};
export const removeNonBasicLatin = text => text.replace(/[^\u0020-\u007F]/g, '');
export const ieFallbackNormalize = text => text.normalize ? text.normalize('NFD') : removeNonBasicLatin(text);
export const slugify = (text = '') => ieFallbackNormalize(text.toString()).replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-');
export const usernameFromEmail = email => email.split('@')[0].replace('.', '-');

/**
 * Return some serializable parts of a hub-http request error.
 *
 * This should work with RavenErrorReportingMiddleware but also be completely
 * serializable so it is suitable for use in an action payload, and doesn't
 * cause problems with react-devtools.
 *
 * If this causes issues, consider using a more robust solution e.g.
 * https://redux-toolkit.js.org/api/serializabilityMiddleware
 *
 * See
 * https://hubspot.slack.com/archives/C0CHG9FGE/p1564498551146000
 */
export const serializableError = error => Object.assign(pick(error, ['stack', 'status', 'statusText', 'data', 'headers', 'responseText', 'responseData', 'responseJSON', 'message', 'code', 'previousError']), {
  options: pick(error.options || {}, ['data']),
  isSerializedError: true
});
const parseQueryString = queryString => parse(queryString.toLowerCase().slice(1));
export const isTrackOrLesson = queryString => {
  const params = parseQueryString(queryString);
  return params && (params.track || params.lesson);
};
export const isNewCatalogUser = queryString => isNewUser(queryString) && !isTrackOrLesson(queryString);
export const getLanguageFromSignup = () => {
  const queryString = window.location.search;
  if (isNewUser(queryString)) {
    const params = parseQueryString(queryString);
    return params ? params.language : undefined;
  }
  return undefined;
};
export const isNonEnglishSelected = queryString => {
  const params = parse(queryString.toLowerCase().slice(1));
  return params && params.language && params.language !== 'en';
};
export const getPlatform = () => {
  if (isHubspotAndroid) {
    return 'android-webview';
  }
  if (isHubSpotNativeApp) {
    return 'ios-webview';
  }
  return undefined;
};
export const waitForGlobal = (window, key, timeout = 100) => {
  const wait = resolve => {
    if (window[key]) {
      resolve(window[key]);
    } else {
      setTimeout(wait.bind(this, resolve), timeout);
    }
  };
  return new Promise(wait);
};
export const parseJSONOrReturn = json => {
  try {
    return JSON.parse(json);
  } catch (e) {
    return json;
  }
};
export const assetIdToAssetType = ({
  lessonId,
  trackId
}) => ({
  assetId: lessonId || trackId || 0,
  assetType: lessonId ? 'LESSON' : 'TRACK'
});
export const getEnv = (win = window) => {
  if (win.location.origin.includes('local')) {
    return 'local';
  } else if (enviro.isQa()) {
    return 'qa';
  } else {
    return 'prod';
  }
};
export const isTrackDeprecated = (deprecated, deprecationFinalDate) => {
  return deprecated && deprecationFinalDate && isTodayOrPast(deprecationFinalDate) || false;
};
export const isTrackDeprecationWarning = (deprecated, deprecationFinalDate, deprecationWarningDate) => {
  return deprecationWarningDate && deprecationFinalDate && isTodayBetweenDays(deprecationWarningDate, deprecationFinalDate) && !isTrackDeprecated(deprecated, deprecationFinalDate) || false;
};
export function isInViewport(element) {
  // this method only takes into account the vertical scroll position of the element,
  // not the horizontal one, which is fine for this use case
  const rect = element.getBoundingClientRect();
  const html = document.documentElement;
  return rect.top >= 0 && rect.top <= (window.innerHeight || html.clientHeight);
}